
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface UseVoiceCommandsOptions {
  onTranscription?: (text: string) => void;
  onError?: (error: Error) => void;
}

export const useVoiceCommands = (options?: UseVoiceCommandsOptions) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      audioChunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudioToText(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.info("Listening...");
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Couldn't access microphone");
      options?.onError?.(error as Error);
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      
      // Stop all tracks from the stream
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      
      setIsRecording(false);
      toast.info("Processing your command...");
      setIsProcessing(true);
    }
  };
  
  const processAudioToText = async (audioBlob: Blob) => {
    try {
      // Convert blob to base64
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          const base64Audio = (reader.result as string).split(',')[1];
          
          // Send to our edge function
          const { data, error } = await supabase.functions.invoke('voice-ai', {
            body: {
              command: 'transcribeAudio',
              params: {
                audio: base64Audio
              }
            }
          });
          
          if (error) throw error;
          
          if (data.text) {
            options?.onTranscription?.(data.text);
            toast.success("Command processed");
          } else {
            toast.error("Couldn't understand audio");
          }
        } catch (error) {
          console.error("Error processing audio:", error);
          toast.error("Failed to process audio");
          options?.onError?.(error as Error);
        } finally {
          setIsProcessing(false);
        }
      };
      
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error("Error processing audio to text:", error);
      setIsProcessing(false);
      options?.onError?.(error as Error);
    }
  };

  // Play text-to-speech audio
  const speakText = async (text: string, voiceId: string = 'pFZP5JQG7iQjIQuC4Bku') => {
    try {
      const { data, error } = await supabase.functions.invoke('voice-ai', {
        body: {
          command: 'generateSpeech',
          params: {
            text,
            voiceId
          }
        }
      });
      
      if (error) throw error;
      
      if (data.audio) {
        const audio = new Audio(`data:audio/mpeg;base64,${data.audio}`);
        audio.play();
        return data.audio;
      }
      
      return null;
    } catch (error) {
      console.error('Error generating speech:', error);
      toast.error('Failed to generate speech');
      options?.onError?.(error as Error);
      return null;
    }
  };
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
        
        if (mediaRecorderRef.current.stream) {
          mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
      }
    };
  }, []);
  
  return {
    isRecording,
    isProcessing,
    startRecording,
    stopRecording,
    speakText
  };
};
